// components/Orders/OrderItemsTable.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The one order-items table the admin and vendor shop-order screens share, and
// the one place that decides which item is selected.
//
// WHY IT EXISTS
// -------------
// Assignment on both screens is per ITEM: `assignOrderToDeliveryAgent(orderId,
// riderId, itemId, ...)` refuses to dispatch without an `itemId`, and the only
// way to set one was a radio in the SEVENTH of nine columns of a table wider
// than the dark panel it sat in. On a laptop that column rendered past the
// right edge, with macOS overlay scrollbars giving no hint that anything was
// there. Staff saw "Assign Order", picked a rider, pressed the button and got
// "Please select an item." with no item in sight. Order
// 6ab87a8c07d7ea2ae7b1199b sat unprocessed for seven hours behind exactly that.
//
// THREE THINGS FIX IT, IN ORDER OF HOW MUCH THEY MATTER
//
//   1. The selector is the FIRST column and `position: sticky; left: 0`. It
//      cannot be scrolled out of view, whatever the table's width.
//   2. The whole row is a click target, and the row carries the selected tint.
//      A 13px radio is not a control anyone should have to aim at.
//   3. When exactly one item can be selected — the overwhelmingly common case,
//      a single-line order — it is selected for you on arrival. The ceremony
//      only exists for multi-item orders, so it should only appear for them.
//
// `onSelect` is still called with the subdocument id the server expects, so
// nothing downstream changes.
//
// SELECTION IDENTITY. Mongoose subdocuments expose `id` (always the
// subdocument's own _id as a string). Brand items have no populated `_id` ref,
// regular items have `_id` populated with the PostListing. `itemKeyOf` is the
// single place that reconciles the two; read it before changing any caller.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { DeliveryStatusBadge } from "../OrderStatusBadges";
import { formatAmount } from "../../utilities/orderSummary";
import { resolveOrderVendor } from "../../utilities/orderTableSorting";

const PLACEHOLDER_IMAGE = "/logo512.png";

/** Statuses whose item can no longer be handed to a rider. */
const LOCKED_ITEM_STATUSES = new Set(["DELIVERED", "REJECTED", "CANCELLED"]);

/**
 * The id the assignment endpoint expects for this line. Returns null when the
 * item carries no usable id, which is the only case a caller must handle.
 */
export const itemKeyOf = (item) => {
  if (!item) return null;
  // The server's own answer (controllers/orderController.js attachLineIds):
  // the subdocument id, kept even when the listing behind `_id` was deleted
  // and populate left null in its place. Everything below is the fallback for
  // payloads from routes that do not emit it yet.
  if (item.lineId) return String(item.lineId);
  if (item.id) return String(item.id);
  if (!item._id) return null;
  if (typeof item._id === "string") return item._id;
  // A populated PostListing ref: its own _id is the subdocument id we want.
  const nested = item._id._id || item._id;
  const text = String(nested);
  return text && text !== "[object Object]" ? text : null;
};

export const isItemSelectable = (item) =>
  !!itemKeyOf(item) &&
  !LOCKED_ITEM_STATUSES.has(String(item?.delivery_status || "").toUpperCase());

/* Vendor shown on the line. Deliberately NOT a local chain: this file used to
   carry its own, which read `_id.creator.businessName` and fell back to
   "In-house", while the orders TABLE carried a different one that fell back to
   "N/A". The same order was described two ways depending on which screen you
   opened. One resolver now answers for both — see
   utilities/orderTableSorting.js, which explains why the fallback is neither
   of those words. */
const vendorNameOf = (item, order) =>
  resolveOrderVendor({ ...order, orderItems: [item] }).label;

const imageOf = (item) =>
  item?.image ||
  item?.imageUrl ||
  (item?.productId && typeof item.productId === "object"
    ? item.productId.imageUrl
    : null) ||
  PLACEHOLDER_IMAGE;

const VendorCell = ({ item, order }) => {
  const name = vendorNameOf(item, order);
  const contact = item?._id?.vendorContactNumber;
  const address = item?._id?.vendorAddress;
  if (!contact && !address) return <>{name}</>;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`vendor-${itemKeyOf(item)}`}>
          {contact && (
            <div>
              <strong>Contact:</strong> {contact}
            </div>
          )}
          {address && (
            <div>
              <strong>Address:</strong> {address}
            </div>
          )}
        </Tooltip>
      }
    >
      <span style={{ borderBottom: "1px dotted #94a3b8", cursor: "help" }}>
        {name}
      </span>
    </OverlayTrigger>
  );
};

/**
 * @param {object}   props.order          the order document
 * @param {string}   props.selectedItemId currently selected subdocument id
 * @param {function} props.onSelect       (itemId) => void
 * @param {boolean} [props.selectable]    false hides the selector column
 *                                        entirely (nothing left to assign)
 * @param {function}[props.onOpenItem]    (item) => void — the Details control
 */
const OrderItemsTable = ({
  order,
  selectedItemId,
  onSelect,
  selectable = true,
  onOpenItem,
}) => {
  const items = order?.orderItems || [];
  const selectableItems = items.filter(isItemSelectable);
  const showSelector = selectable && selectableItems.length > 0;

  /* One selectable item means there is nothing to choose. Selecting it on
     arrival turns a two-step ritual into one press of Assign, and removes the
     single most common way this screen used to dead-end.

     `autoSelectId` is a plain string, so the effect below has complete,
     primitive dependencies and needs no exhaustive-deps escape hatch. That
     matters beyond tidiness: the CRA build resolves an eslint that does not
     register the react-hooks plugin, so an `eslint-disable` naming one of its
     rules is an unknown-rule ERROR and the production build fails. */
  const autoSelectId =
    showSelector && selectableItems.length === 1
      ? itemKeyOf(selectableItems[0])
      : null;

  // A ref keeps the callback out of the dependency list whether the caller
  // passes a stable setter or a fresh closure each render.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!autoSelectId) return;
    if (selectedItemId === autoSelectId) return;
    if (onSelectRef.current) onSelectRef.current(autoSelectId);
  }, [autoSelectId, selectedItemId]);

  if (!items.length) {
    return (
      <div className="rod-hint" style={{ padding: "14px 0" }}>
        This order has no items.
      </div>
    );
  }

  return (
    <div className="rod-table-wrap">
      <table className="rod-data-table">
        <thead>
          <tr>
            {showSelector && (
              <th className="rod-select-col" scope="col">
                <span className="sr-only">Select</span>
              </th>
            )}
            <th className="index-col" scope="col">
              #
            </th>
            <th scope="col">Item</th>
            <th scope="col" style={{ textAlign: "center" }}>
              Qty
            </th>
            <th scope="col" style={{ textAlign: "right" }}>
              Unit price
            </th>
            <th scope="col" style={{ textAlign: "right" }}>
              Line total
            </th>
            <th scope="col">Status</th>
            {onOpenItem && (
              <th scope="col" style={{ textAlign: "center" }}>
                Details
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const key = itemKeyOf(item) || `row-${index}`;
            const canSelect = showSelector && isItemSelectable(item);
            const selected = !!key && key === selectedItemId;
            const qty = Number(item?.qty) > 0 ? Number(item.qty) : 1;
            // The line as sold, from the server's order read (name with the
            // chosen options, unit price and total including them). The base
            // price is only a fallback for an older read.
            const price = Number(item?.lineUnitPrice ?? item?.price) || 0;
            const lineTotal = Number(item?.lineTotal ?? price * qty) || 0;
            const name = item?.lineLabel || item?.name;

            const rowClass = [
              selected ? "rod-row-selected" : "",
              canSelect ? "rod-row-selectable" : "",
              showSelector && !canSelect ? "rod-row-locked" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <tr
                key={key}
                className={rowClass}
                onClick={canSelect ? () => onSelect(key) : undefined}
              >
                {showSelector && (
                  <td className="rod-select-col">
                    <input
                      type="radio"
                      className="rod-select-radio"
                      name="orderItemSelection"
                      value={key}
                      checked={selected}
                      disabled={!canSelect}
                      onChange={() => canSelect && onSelect(key)}
                      aria-label={`Select ${item?.name || `item ${index + 1}`}`}
                      title={
                        canSelect
                          ? "Select this item to assign or dispatch it"
                          : "This item is already finished"
                      }
                    />
                  </td>
                )}
                <td className="index-col">{index + 1}</td>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <img
                      src={imageOf(item)}
                      alt=""
                      className="rod-item-thumb"
                      onError={(e) => {
                        e.target.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div className="rod-item-name">{name}</div>
                      <div className="rod-item-vendor">
                        <VendorCell item={item} order={order} />
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ textAlign: "center" }}>{qty}</td>
                <td style={{ textAlign: "right" }}>{formatAmount(price)}</td>
                <td className="total-col">{formatAmount(lineTotal)}</td>
                <td>
                  <DeliveryStatusBadge status={item?.delivery_status} />
                </td>
                {onOpenItem && (
                  <td style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="lk-action-btn lk-action-btn--quiet"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenItem(item);
                      }}
                    >
                      View
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OrderItemsTable;
